// Import Vuetify first, so that their CSS rules are applied before those in our own components.
import './plugins/vuetify';

import router from './routes';
import { setUserId } from './plugins/analytics';
import Vue from 'vue';
import { initializeApp } from 'firebase/app';
import { getMessaging, isSupported } from 'firebase/messaging';
import './plugins/CkEditor';
import App from './App.vue';
import store from './store/store';
import { OktaAuth } from '@okta/okta-auth-js';
import vueScrollto from 'vue-scrollto';
import highlight from 'vue-highlight-text/public/directive.min.js';
import Croppa from 'vue-croppa';
import infiniteScroll from 'vue-infinite-scroll';
import axios from 'axios';
import { datadogRum } from '@datadog/browser-rum';
import * as VueGoogleMaps from 'vue2-google-maps';
import { oktaAuthConfig } from './store/store.constant';
import { appConstant } from './app.constant';
import { storeGetters } from "@/store/storeGetters.constant";
import { storeMutation } from "@/store/storeMutations.constant";
import { storeAction } from "./store/storeActions.constant";
import { routerNames } from "@/components/appCommon.constants";
import { removeLastChar } from "@/common/utils/utils";

import '@fortawesome/fontawesome-free/css/all.css';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';

Vue.config.productionTip = false;
export const eventBus = new Vue();

const authClient = new OktaAuth(oktaAuthConfig);

authClient.tokenManager.get('accessToken')
    .then((token) => {
        if (token) {
            sessionStorage.setItem('loggedUserYpo', 'user authenticated!');
            const accessTokenData = token.accessToken && authClient.token.decode(token.accessToken);
            if (accessTokenData && accessTokenData.payload.ypoId) {
                setUserId(accessTokenData.payload.ypoId);
            }
        }
    });

Vue.directive('highlight', highlight);
Vue.use(vueScrollto);
Vue.use(Croppa);
Vue.use(infiniteScroll);

Vue.use(VueGoogleMaps, {
    load: {
        key: process.env.VUE_APP_GMAP_API_KEY,
        libraries: 'places', // necessary for places input
    },
});

// See: https://firebase.google.com/docs/web/learn-more#config-object
const config = {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
    authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.VUE_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.VUE_APP_FIREBASE_APP_ID,
};

localStorage.setItem(appConstant.DEFAULT_IS_FIREBASE_SUPPORTED, appConstant.DEFAULT_FIREBASE_SUPPORTED_FALSE);
 isSupported()
     .then((supported) => {
         if (supported) {
             localStorage.setItem(appConstant.DEFAULT_IS_FIREBASE_SUPPORTED, appConstant.DEFAULT_FIREBASE_SUPPORTED_TRUE);
             // Initialize Firebase
             const firebaseApp = initializeApp(config);
             // Initialize Firebase Cloud Messaging and get a reference to the service
             Vue.prototype.$messaging = getMessaging(firebaseApp);
         } else {
             localStorage.setItem(appConstant.DEFAULT_IS_FIREBASE_SUPPORTED, appConstant.DEFAULT_FIREBASE_SUPPORTED_FALSE);
         }
     })
     .catch((err) => {
         console.error('Error checking Firebase Messaging:', err);
     });

const excludedRoutesFromInterceptors = [`#/${routerNames.SPEAKER}`, '/errorPag', '/version'];

axios.interceptors.request.use(async (req) => {
    const authData = store.getters[`auth/${storeGetters.authData}`];
    const tokenRefreshTime = store.getters[`auth/${storeGetters.tokenRefreshTime}`];
    const inInLoginRedirectMode = sessionStorage.getItem(appConstant.storageKeys.loginRedirectModeTriggered);
    if (!inInLoginRedirectMode) {
        const accessToken = await authClient.tokenManager.get("accessToken");
        const locationUrlHash = window.location.hash;
        if (!accessToken && !excludedRoutesFromInterceptors.includes(locationUrlHash)) {
            await store.dispatch(`auth/${storeAction.initiateLoginRedirect}`);
            return;
        }
    }
    if (authData && authData.expTime) {
        if (authData.authProvider === "okta") {
            // expiration time should be less than 1 hour and more than 30 sec
            if ((authData.expTime - new Date() < tokenRefreshTime)) {
                if (authData.expTime - new Date() > 30000) {
                    store.dispatch(`auth/${storeAction.refreshOktaAccessToken}`).then(() => {
                    });
                } else {
                    store.commit(storeMutation.setInactiveSessionDetection, true, { root: true });
                }
            }
        }
    }
    return req;
});

datadogRum.init({
    applicationId: process.env.VUE_APP_DATADDOG_APPLICATION_ID || 'f42de518-2c0c-4bbe-89cd-fda1820caca8',
    clientToken: process.env.VUE_APP_DATADDOG_CLIENT_TOKEN || 'pub0ddeb21d40e778ba172098514f6c200a',
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'us3.datadoghq.com',
    service: process.env.VUE_APP_DATADDOG_SERVICE_NAME || 'ypoconnect-ui-test',
    env: process.env.VUE_APP_DATADDOG_ENV || 'test',
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: Number(process.env.VUE_APP_DATADDOG_SESSION_SAMPLE_RATE) || 100,
    sessionReplaySampleRate: Number(process.env.VUE_APP_DATADDOG_SESSION_REPLAY_SAMPLE_RATE) || 90,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [
        removeLastChar(process.env.VUE_APP_BASE_URL || "https://test-api.ypoconnect.org/"),
        /https:\/\/.*\.ypoconnect\.org/, // Allows any subdomain under ypoconnect.org
        url => url.startsWith(removeLastChar(process.env.VUE_APP_BASE_URL || "https://test-api.ypoconnect.org/")),
    ],
});

new Vue({
    router,
    store,
    render: h => h(App),
}).$mount('#app');
